/*
 * CareMatch Product Recommendation V1
 * Contract: docs/specs/CAREMATCH_PRODUCT_RECOMMENDATION_V1.md
 * Deterministic, theme-layer only: reads public /collections/<handle>/products.json.
 */
(function (root) {
  'use strict';

  var MAX_RECS = 3;
  var MAX_PAGES = 3; // 250 products per page

  // Normalized hint -> matcher over title + product_type (INV-040: no `ramps`, no joined strings).
  var HINTS = {
    'grab-bars':           { re: /grab[\s-]?bars?/i,                                   label: 'grab bars' },
    'non-slip':            { re: /non[\s-]?slip|anti[\s-]?(slip|skid)/i,               label: 'slip resistance' },
    'shower-chairs':       { re: /shower (chair|stool|seat)|bath (bench|seat|chair)/i, label: 'shower seating' },
    'transfer-benches':    { re: /transfer bench/i,                                    label: 'transfer support' },
    'raised-toilet-seats': { re: /raised toilet|toilet (seat|safety|support) (rail|frame)?|commode/i, label: 'toilet safety' },
    'canes':               { re: /\bcanes?\b/i,                                         label: 'a cane' },
    'walkers':             { re: /\bwalkers?\b/i,                                       label: 'a walker' },
    'rollators':           { re: /\brollators?\b/i,                                     label: 'a walker' },
    'wheelchairs':         { re: /\bwheelchairs?\b/i,                                   label: 'a wheelchair' },
    'cushions':            { re: /cushion/i,                                            label: 'seating comfort' }
  };

  // Parts/accessories are demoted, not excluded: they stay eligible but rank below whole products.
  var ACCESSORY = /\b(only|fits|replacement|refills?|inserts?|tips?|bulbs?|baskets?|liners?|bags?|pouch(es)?|holders?|carriers?|trays?|caps?|coasters?|glides?|brakes?|boots?|kits?|assembly|accessor(y|ies)|parts?)\b|\b(walker|rollator|wheelchair)s? wheels?\b/i;

  var GOAL_REASON = {
    stability: 'Matches your safer-walking goal.',
    bath_safety: 'Matches your bathroom-safety goal.',
    daily_independence: 'Matches your daily-independence goal.',
    recovery: 'Matches your recovery goal.',
    comfort_relief: 'Matches your comfort and pain-relief goal.'
  };

  // Normalize the existing mapper's tag output: split joined strings, trim, lower, dedupe, drop unknown.
  function normalizeTags(raw) {
    var out = [];
    (raw || []).forEach(function (t) {
      String(t).split(',').forEach(function (p) {
        var k = p.trim().toLowerCase();
        if (k && HINTS[k] && out.indexOf(k) === -1) out.push(k);
      });
    });
    return out;
  }

  // Which answers produced each hint (for deterministic explanations).
  function hintSources(d) {
    var s = {};
    function add(tag, src) { if (!s[tag]) s[tag] = src; }
    if (d.mobility === 'cane') add('canes', 'mobility');
    if (d.mobility === 'walker') { add('walkers', 'mobility'); add('rollators', 'mobility'); }
    if (d.mobility === 'wheelchair') { add('wheelchairs', 'mobility'); add('cushions', 'mobility'); }
    var bn = d.bath_needs || [];
    if (bn.indexOf('seat') > -1) add('shower-chairs', 'bath');
    if (bn.indexOf('transfer') > -1) add('transfer-benches', 'bath');
    if (bn.indexOf('toilet') > -1) add('raised-toilet-seats', 'bath');
    if (bn.indexOf('grabbars') > -1) add('grab-bars', 'bath');
    if ((d.zones || []).indexOf('home_entry') > -1 || d.fall_risk === 'high') { add('grab-bars', 'fall'); add('non-slip', 'fall'); }
    return s;
  }

  function handleOf(url) { return String(url).split('/').filter(Boolean).pop(); }

  function fetchCollection(handle) {
    var all = [];
    function page(n) {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (j) {
          var ps = (j && j.products) || [];
          all = all.concat(ps);
          if (ps.length === 250 && n < MAX_PAGES) return page(n + 1);
          return all;
        })
        .catch(function () { return all; });
    }
    return page(1);
  }

  function isEligible(p) {
    if (!p || !p.handle || !p.variants || !p.variants.length) return false;
    var priced = p.variants.filter(function (v) { return parseFloat(v.price) > 0; });
    if (!priced.length) return false;                      // zero-price / draft-only condition
    return priced.some(function (v) { return v.available; });  // purchasable in current storefront state
  }

  // Product family = first two significant words after dropping brands, sizes and colours,
  // so size/colour variants of one item ("Wood Cane 1 x36 Mahogany" / "... Black") collapse.
  var BRANDS = /^(mckesson|drive|mabis|medline|posey|dycem|carex|nova|blue jay|essential|lumex)\b\s*/;
  function family(title) {
    var t = String(title).toLowerCase().replace(/[®™]/g, '')
      .replace(/\b\d+(\s*[\/.]\s*\d+)?\s*(x\s*\d+(\s*[\/.]\s*\d+)?)*\b/g, ' ')
      .replace(/[^a-z\s]/g, ' ')
      .replace(/\b(inch|in|foot|ft|lbs?|oz|xx|x|small|medium|large|each|pack|case|box|pair|cs|retail|white|blue|black|gray|grey|red|silver|bronze|purple|green|pink|beige|tan|chrome|mahogany|soft)\b/g, ' ')
      .replace(/\s+/g, ' ').trim();
    t = t.replace(BRANDS, '');
    return t.split(' ').slice(0, 2).join(' ');
  }

  function money(v) {
    var n = parseFloat(v);
    return isNaN(n) ? '' : '$' + n.toFixed(2);
  }

  function profileKey(d, handles, tags) {
    return ['g:' + (d.goal || 'none'), 'c:' + handles.join('+'), 't:' + (tags.join('+') || 'none')].join('|');
  }

  /**
   * rank(d, recUrls, rawTags, productsByHandle) -> { items, handles, tags, profile_key }
   * productsByHandle: { collectionHandle: [products in merchandising order] }
   */
  function rank(d, recUrls, rawTags, productsByHandle, titles) {
    var handles = recUrls.map(handleOf);
    var tags = normalizeTags(rawTags);
    var src = hintSources(d);
    var goalHandle = {
      stability: 'mobility-aids', bath_safety: 'bath-safety-comfort', daily_independence: 'daily-living-aids',
      recovery: 'physical-therapy-supplies', comfort_relief: 'pain-management-pressure-relief'
    }[d.goal];

    var cand = {};
    handles.forEach(function (h, ci) {
      (productsByHandle[h] || []).forEach(function (p, pi) {
        var c = cand[p.handle];
        if (!c) c = cand[p.handle] = { p: p, cols: [], order: ci * 10000 + pi };
        if (c.cols.indexOf(h) === -1) c.cols.push(h);
      });
    });

    var scored = Object.keys(cand).map(function (k) {
      var c = cand[k], p = c.p, score = 0, matched = [];
      if (!isEligible(p)) return null;
      if (c.cols.indexOf(handles[0]) > -1) score += 100;
      score += 30 * c.cols.filter(function (h) { return h !== handles[0]; }).length;
      var text = p.title + ' ' + (p.product_type || '');
      tags.forEach(function (t) { if (HINTS[t].re.test(text) && matched.length < 3) matched.push(t); });
      score += 15 * matched.length;
      var accessory = ACCESSORY.test(p.title);
      if (accessory) score -= 40;
      return { p: p, cols: c.cols, order: c.order, score: score, matched: matched, accessory: accessory };
    }).filter(Boolean);

    scored.sort(function (a, b) { return b.score - a.score || a.order - b.order; });

    // Greedy pick: skip same product family; prefer a candidate that answers a not-yet-covered selection.
    var picked = [], fams = [], covered = [];
    function eff(c) {
      if (!c.matched.length) return c.score;
      var fresh = c.matched.some(function (t) { return covered.indexOf(t) === -1; });
      return c.score + (fresh ? 10 : -10);
    }
    var pool = scored.slice();
    while (picked.length < MAX_RECS && pool.length) {
      var best = -1;
      for (var i = 0; i < pool.length; i++) {
        if (fams.indexOf(family(pool[i].p.title)) > -1) continue;
        if (best === -1 || eff(pool[i]) > eff(pool[best]) ||
            (eff(pool[i]) === eff(pool[best]) && pool[i].order < pool[best].order)) best = i;
      }
      if (best === -1) break;
      var c = pool.splice(best, 1)[0];
      fams.push(family(c.p.title)); picked.push(c);
      c.matched.forEach(function (t) { if (covered.indexOf(t) === -1) covered.push(t); });
    }

    var items = picked.map(function (s) {
      var reasons = [];
      if (s.matched.length) {
        var t = s.matched[0];
        reasons.push(src[t] === 'fall'
          ? 'Included because of your fall-risk answers.'
          : 'Shown because you selected ' + HINTS[t].label + '.');
      }
      if (goalHandle && s.cols.indexOf(goalHandle) > -1 && GOAL_REASON[d.goal]) reasons.push(GOAL_REASON[d.goal]);
      else reasons.push('Part of the ' + ((titles && titles[s.cols[0]]) || 'recommended') + ' collection matched to your answers.');
      var img = s.p.images && s.p.images[0];
      return {
        handle: s.p.handle,
        title: s.p.title,
        url: '/products/' + s.p.handle,
        price: money((s.p.variants.filter(function (v) { return v.available && parseFloat(v.price) > 0; })[0] || s.p.variants[0]).price),
        image: img ? img.src : '',
        reason: reasons.slice(0, 2).join(' '),
        score: s.score
      };
    });

    return { items: items, handles: handles, tags: tags, profile_key: profileKey(d, handles, tags) };
  }

  function recommend(d, recUrls, rawTags, titles) {
    var handles = recUrls.map(handleOf);
    return Promise.all(handles.map(fetchCollection)).then(function (lists) {
      var by = {};
      handles.forEach(function (h, i) { by[h] = lists[i]; });
      return rank(d, recUrls, rawTags, by, titles);
    });
  }

  // ---------- Rendering + analytics (theme layer) ----------

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function push(evt) {
    root.dataLayer = root.dataLayer || [];
    root.dataLayer.push(evt);
  }

  // Fallback: the existing mapper returned no collection.
  function fallback(d, container) {
    push({ event: 'carematch_product_recs_fallback', profile_key: profileKey(d, [], []) });
    if (container) {
      container.innerHTML = '<p class="cm-shortlist-empty">Browse our collections to find available options.</p>';
    }
  }

  /**
   * render(container, d, recUrls, rawTags, titles)
   * Shows up to three products below the collection recommendations.
   * Never renders an empty card list; failures leave the collection recommendations intact.
   */
  function render(container, d, recUrls, rawTags, titles) {
    if (!container) return Promise.resolve();
    if (!recUrls || !recUrls.length) { fallback(d, container); return Promise.resolve(); }
    container.innerHTML = '<p class="cm-shortlist-loading">Finding products that fit your answers…</p>';
    return recommend(d, recUrls, rawTags, titles).then(function (r) {
      if (!r.items.length) {
        container.innerHTML = '<p class="cm-shortlist-empty">Browse this recommended category for available options.</p>';
        push({ event: 'carematch_product_recs_empty', recommended_collection_handles: r.handles, profile_key: r.profile_key });
        return;
      }
      var cards = r.items.map(function (it, i) {
        return '<li class="cm-prod">' +
          '<a class="cm-prod-link" href="' + esc(it.url) + '" data-cm-pos="' + (i + 1) + '" data-cm-handle="' + esc(it.handle) + '">' +
            (it.image ? '<img class="cm-prod-img" src="' + esc(it.image + (it.image.indexOf('?') > -1 ? '&' : '?') + 'width=240') + '" alt="" loading="lazy" width="120" height="120">' : '') +
            '<span class="cm-prod-body">' +
              '<span class="cm-prod-title">' + esc(it.title) + '</span>' +
              (it.price ? '<span class="cm-prod-price">' + esc(it.price) + '</span>' : '') +
              '<span class="cm-prod-why">' + esc(it.reason) + '</span>' +
              '<span class="cm-prod-cta">View product</span>' +
            '</span>' +
          '</a>' +
        '</li>';
      }).join('');
      container.innerHTML =
        '<h3 class="cm-shortlist-title">Products to start with</h3>' +
        '<ul class="cm-prods">' + cards + '</ul>' +
        '<p class="cm-shortlist-note">Suggestions are general guidance based on your answers, not medical advice. Check product details, and ask your clinician if you are unsure.</p>';
      push({
        event: 'carematch_product_recs_view',
        recommendation_count: r.items.length,
        recommended_collection_handles: r.handles,
        product_handles: r.items.map(function (it) { return it.handle; }),
        profile_key: r.profile_key
      });
      container.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a.cm-prod-link');
        if (!a) return;
        push({
          event: 'carematch_product_recommendation_click',
          product_handle: a.getAttribute('data-cm-handle'),
          position: parseInt(a.getAttribute('data-cm-pos'), 10),
          recommended_collection_handles: r.handles,
          profile_key: r.profile_key
        });
      });
    }).catch(function () {
      container.innerHTML = '';
    });
  }

  root.CareMatchRecs = { normalizeTags: normalizeTags, rank: rank, recommend: recommend, render: render, fallback: fallback, isEligible: isEligible, family: family, HINTS: HINTS };
})(typeof window !== 'undefined' ? window : this);
